<template>
  <div class="tw:flex tw:flex-col tw:flex-1 tw:md:p-4 tw:p-2 tw:w-full tw:min-h-0">
    <el-skeleton :loading="loading" animated class="tw:flex-1">
      <template #template>
        <div class="tw:flex tw:flex-wrap tw:gap-2">
          <el-skeleton-item v-for="index in 10" :key="index" style="width: 160px; height: 238px" />
        </div>
      </template>
      <template #default>
        <div class="tw:flex tw:flex-col tw:gap-2">
          <div class="tw:flex tw:flex-1 tw:h-full tw:min-h-0 tw:overflow-y-auto">
            <template v-if="filteredTokens.length > 0 || filteredNftTokens.length > 0">
              <div class="tw:flex tw:flex-wrap tw:justify-center tw:gap-2 tw:w-full">
                <StorageItem
                  v-for="token in filteredTokens"
                  :key="token.id"
                  :token="token"
                  @click.stop="handleClick(token)"
                />
                <StorageItem
                  v-for="nftToken of filteredNftTokens"
                  :key="nftToken.name"
                  :nft-token="nftToken"
                  @click.stop="handleClickNFT(nftToken)"
                />
              </div>
            </template>
            <template v-else>
              <div class="tw:flex tw:flex-col tw:flex-1 tw:justify-center tw:items-center tw:gap-6 tw:min-h-[300px]">
                <div class="tw:flex tw:flex-col tw:items-center">
                  <h2 class="tw:text-white tw:text-2xl">{{ $t('walletHoldIsEmpty') }}</h2>
                  <p>{{ $t('walletTryChangingFilters') }}</p>
                </div>
                <HalloweenGenericButton @click="$emit('reset')">{{ $t('walletReset') }}</HalloweenGenericButton>
              </div>
            </template>
          </div>
        </div>
      </template>
    </el-skeleton>
    <TransferTokenModal v-model="showTransferTokenModal" :token="transferToken" />
    <TransferNFTModal v-model="showTransferNFTModal" :token="transferNFTToken" />
  </div>
</template>

<script lang="ts" setup>
import type { StorageItem } from '~/types/apiService';
import type { TokenDescription } from '~/types/token';
import { tokensConfig } from '~/utils/constants';
import { Formatters } from '~/utils/formatters/formatters';
import { TransferNFTModal, TransferTokenModal } from '~/widgets/wallet';
import { TOKEN_TAB } from './StorageWrapper.vue';

const {
  loading = false,
  tokens,
  search,
  tokenTab,
  withBalanceOnly,
  nftTokens
} = defineProps<{
  tokens: StorageItem[];
  tokenTab: TOKEN_TAB;
  nftTokens: TokenDescription[];
  withBalanceOnly: boolean;
  loading?: boolean;
  search: string;
}>();

const emit = defineEmits<{
  reset: [];
  innerModalOpen: [];
  innerModalClose: [];
}>();

const { t } = useI18n();

const showTransferTokenModal = ref(false);
const transferToken = ref<StorageItem | null>(null);
const showTransferNFTModal = ref(false);
const transferNFTToken = ref<TokenDescription | null>(null);

watch([() => tokens, () => nftTokens], ([newTokens, newNftTokens]) => {
  if (transferToken.value) {
    transferToken.value = newTokens.find((token) => token.id === transferToken.value?.id) || null;
  }
  if (transferNFTToken.value) {
    transferNFTToken.value = newNftTokens.find((nftToken) => nftToken.name === transferNFTToken.value?.name) || null;
  }
});

watch(showTransferTokenModal, (value) => {
  if (value) {
    emit('innerModalOpen');
  } else {
    emit('innerModalClose');
  }
});

const filteredTokens = computed(() => {
  let result = [...tokens];

  if (search) {
    const searchLower = search.toLowerCase();
    result = result.filter((token) => {
      const localizedName = t(tokensConfig[token.key]?.label || '', { count: 0 });
      return localizedName.toLowerCase().includes(searchLower);
    });
  }

  if (withBalanceOnly) {
    result = result.filter((token) => Number(token.balance) > 0);
  }

  switch (tokenTab) {
    case TOKEN_TAB.NFTs:
      result = result.filter((token) => token.isNft);
      break;
    case TOKEN_TAB.MINED:
      result = result.filter((token) => token.isSellable);
      break;
    case TOKEN_TAB.CRAFTED:
      result = result.filter((token) => !token.isSellable);
      break;
    default:
      break;
  }

  return result;
});

const filteredNftTokens = computed(() => {
  if (tokenTab !== TOKEN_TAB.NFTs && tokenTab !== TOKEN_TAB.ALL) return [];

  let result = [...nftTokens];

  if (search) {
    const searchLower = search.toLowerCase();
    result = result.filter((nftToken) => {
      const translationKey = Formatters.text.formatTranslationKey(nftToken.name);
      const localizedName = t(translationKey || '', { count: 0 });
      return localizedName.toLowerCase().includes(searchLower);
    });
  }

  return result;
});

const handleClick = (token: StorageItem) => {
  transferToken.value = token;
  showTransferTokenModal.value = true;
};

const handleClickNFT = (nftToken: TokenDescription) => {
  transferNFTToken.value = nftToken;
  showTransferNFTModal.value = true;
};
</script>

<style lang="scss" scoped>
.card.storage {
  max-height: calc(100vh - 250px);
  opacity: 1;
  padding: 25px;
  transition: 300ms ease all;
  scrollbar-width: thin;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.navbar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 25px;
  font-size: 32px;
  color: #fff;
}
</style>

<!-- popper custom styles -->
<style>
.customPopper {
  padding: 20px !important;
  border-radius: 15px !important;
  border: 1px solid #2f2f2f !important;
  background: rgba(0, 0, 0, 0.85) !important;
}
</style>
